import PptxGenJS from "pptxgenjs";
import { addTitleSlide } from "./slides/titleSlide";
import {
  TITLE_MASTER,
  titleMasterSlide,
} from "./masters/title-with-background";
import {
  SPLIT_RIGHT_MASTER,
  splitRightMasterSlide,
} from "./masters/split-right";
import { addInstructionsSlide } from "./slides/instructionsSlide";
import { SPLIT_LEFT_MASTER, splitLeftMasterSlide } from "./masters/split-left";
import { addTalentDashboardSlide } from "./slides/talentDashboardSlide";
import { SPLIT_TOP_MASTER, splitTopMasterSlide } from "./masters/split-top";
import { addSummarySlide } from "./slides/summarySlide";
import { addExperiencesSlide } from "./slides/experiencesSlide";
import { addExecutivePotentialSlide } from "./slides/executivePotentialSlide";
import { addLeadershipSpanSlide } from "./slides/leadershipSpanSlide";
import {
  SPLIT_TOP_THREE_MASTER,
  splitTopThreeMasterSlide,
} from "./masters/split-top-three";
import {
  logoCenterMasterSlide,
  LOGO_CENTER_MASTER,
} from "./masters/logo-center";
import { addEndSlide } from "./slides/endSlide";
import { addPsychSummarySlide } from "./slides/psychSummarySlide";
import { addPsychInstructionsSlide } from "./slides/psychInstructionsSlide";
import { addPsychTitleSlide } from "./slides/psychTitleSlide";
import { addPsychLeadershipSpanSlide } from "./slides/psychLeadershipSpanSlide";
import {
  TITLE_MASTER_PSYCH,
  titleMasterSlidePsych,
} from "./masters/title-with-background-psych";
import {
  SPLIT_BOTTOM_SMALL_MASTER,
  splitBottomSmallMasterSlide,
} from "./masters/split-bottom-small";
import { DimensionWithScores } from "@/features/people/hooks/useDimensionsWithScores";
import { hoganPersonalityInventorySlide } from "./slides/hoganPersonalityInventory";
import { hoganDevelopmentSurveySlide } from "./slides/hoganDevelopmentSurvey";
import { hoganMotivesValuesPreferencesSlide } from "./slides/hoganMotivesValuesPreferences";
import { opqScoresSlide } from "./slides/opqScoresSlide";
// Define a type for slide data to be passed
export interface TemplateData {
  title?: string;
  candidateName?: string;
  candidatePositionRole?: string;
  companyName?: string;
  positionTitle?: string;
  evaluationRows?: Record<string, any[]>;
  keyStrengths?: string;
  keyDevelopmentOpportunities?: string;
  likelyStrengths?: string;
  areasToProbe?: string;
  createdBy?: string;
  leadershipSpan?: DimensionWithScores[];
  dimensionsData?: Record<string, DimensionWithScores[]>;
}

// Type for the media resolver function that will be passed to slide generators
export type MediaResolver = (path: string) => string | undefined;

// Custom Master Slide Properties that include our internal path references
export interface CustomMasterSlideProps extends PptxGenJS.SlideMasterProps {
  _backgroundImagePath?: string; // Path to lookup in media.json
}

export interface PresentationTemplate {
  name: string;
  masterSlides: Record<string, CustomMasterSlideProps>; // Use our custom type for masters
  slides: Array<(pres: PptxGenJS, data: any) => void>;
}

const masterSlides: Record<string, CustomMasterSlideProps> = {
  [TITLE_MASTER]: titleMasterSlide,
  [TITLE_MASTER_PSYCH]: titleMasterSlidePsych,
  [SPLIT_RIGHT_MASTER]: splitRightMasterSlide,
  [SPLIT_LEFT_MASTER]: splitLeftMasterSlide,
  [SPLIT_TOP_MASTER]: splitTopMasterSlide,
  [SPLIT_TOP_THREE_MASTER]: splitTopThreeMasterSlide,
  [LOGO_CENTER_MASTER]: logoCenterMasterSlide,
  [SPLIT_BOTTOM_SMALL_MASTER]: splitBottomSmallMasterSlide,
};

export const rraIndividualAssessmentTemplate: PresentationTemplate = {
  name: "RRA Individual Assessment",
  masterSlides,
  slides: [
    addTitleSlide,
    addInstructionsSlide,
    addTalentDashboardSlide,
    addSummarySlide,
    addExperiencesSlide,
    addLeadershipSpanSlide,
    addExecutivePotentialSlide,
    addEndSlide,
  ],
};

export const rraPsychOnlyReportTemplate: PresentationTemplate = {
  name: "RRA Psych Only Report",
  masterSlides,
  slides: [
    addPsychTitleSlide,
    addPsychInstructionsSlide,
    addPsychSummarySlide,
    addPsychLeadershipSpanSlide,
    hoganPersonalityInventorySlide,
    hoganDevelopmentSurveySlide,
    hoganMotivesValuesPreferencesSlide,
    opqScoresSlide,
    addEndSlide,
  ],
};

export const templates: Record<string, PresentationTemplate> = {
  "rra-individual-assessment": rraIndividualAssessmentTemplate,
  "rra-psych-only-report": rraPsychOnlyReportTemplate,
};

export const getTemplate = (
  templateName: string
): PresentationTemplate | undefined => templates[templateName];
